import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QualifyMissingPlaceDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.from('place_suggestions').insert({
        suggestion_type: 'place',
        suggested_name: name.trim(),
        city: city.trim() || null,
        region: region.trim() || null,
        note: note.trim() || null,
        producer_id: null as any,
      });

      if (error) throw error;

      toast({
        title: 'Tack för ditt förslag!',
        description: 'Vi går igenom det och återkommer.',
      });
      onOpenChange(false);
    } catch (err) {
      console.error('Place suggestion error:', err);
      toast({
        title: 'Något gick fel',
        description: 'Försök igen om en stund.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Föreslå en plats</DialogTitle>
          <DialogDescription>
            Berätta vilken plats du tillhör så lägger vi till den.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="suggest-name">Platsens namn *</Label>
            <Input
              id="suggest-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="t.ex. Leksands Fäbod"
              required
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="suggest-city">Ort</Label>
              <Input
                id="suggest-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="t.ex. Leksand"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="suggest-region">Region</Label>
              <Input
                id="suggest-region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="t.ex. Dalarna"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="suggest-note">Övrig information</Label>
            <Textarea
              id="suggest-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Berätta gärna vad som gör platsen speciell…"
              rows={2}
            />
          </div>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Skickar…' : 'Skicka förslag'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
